/* Churn radar widget for the /admin Overview tab.
 * Lists active enrollments with no activity in the configured window. */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, Mail, RefreshCw } from 'lucide-react';

const API = (process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL);
const api = axios.create({ baseURL: API, withCredentials: true });

export default function ChurnRadarWidget({ adminPassword }) {
  const [authed, setAuthed] = useState(false);
  const [days, setDays] = useState(14);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // Get JWT cookie via admin password (same pattern as AcademyAdminPanel)
  useEffect(() => {
    if (!adminPassword) return;
    let active = true;
    (async () => {
      try {
        await api.post('/api/auth/login', { email: 'admin@zhelta.com', password: adminPassword });
        if (active) setAuthed(true);
      } catch {
        if (active) setAuthed(false);
      }
    })();
    return () => { active = false; };
  }, [adminPassword]);

  const reload = useCallback(async () => {
    if (!authed) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/api/academy/admin/churn-radar?days=${days}`);
      setRows(data.rows || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [authed, days]);

  useEffect(() => { reload(); }, [reload]);

  return (
    <section className="bg-white rounded-2xl border border-[#E5E5E7] p-5 lg:p-6" data-testid="churn-radar-widget">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-[#c9a227]" />
          <h3 className="text-[14px] font-bold tracking-[0.18em] uppercase text-[#1D1D1F]">Churn Radar</h3>
          <span className="text-[11px] text-[#6E6E73]">{rows.length} flagged</span>
        </div>
        <div className="flex items-center gap-2">
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}
            data-testid="churn-radar-days"
            className="text-[12px] px-2 py-1 rounded-md border border-[#E5E5E7] focus:border-[#c9a227] outline-none">
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
            <option value={21}>21 days</option>
            <option value={30}>30 days</option>
          </select>
          <button onClick={reload} aria-label="Refresh"
            className="text-[#6E6E73] hover:text-[#c9a227]"
            data-testid="churn-radar-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {!authed ? (
        <div className="text-[#6E6E73] text-[13px]">Authenticating…</div>
      ) : loading ? (
        <div className="text-[#6E6E73] text-[13px]">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-[13.5px] text-[#6E6E73] py-3">
          🌟 No stagnant enrollments in the last {days} days. Cohort is healthy.
        </div>
      ) : (
        <ul className="divide-y divide-[#F0F0F0]">
          {rows.slice(0, 8).map((r) => (
            <li key={`${r.user_id}-${r.program_slug}`} data-testid={`churn-row-${r.user_id}`} className="py-2.5 flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-[13.5px] font-bold text-[#1D1D1F] truncate">
                  {r.name || r.email} <span className="text-[#6E6E73] font-normal">· {r.program_name}</span>
                </div>
                <div className="text-[11.5px] text-[#6E6E73]">
                  {r.tier_id} · {r.progress || 0}% complete · stale {r.stale_days}d
                </div>
              </div>
              <a href={`mailto:${r.email}?subject=Checking%20in%20on%20your%20${encodeURIComponent(r.program_name || 'course')}`}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider text-[#a8871a] bg-amber-50 hover:bg-amber-100 border border-amber-200"
                data-testid={`churn-nudge-${r.user_id}`}>
                <Mail className="w-3 h-3" /> Nudge
              </a>
            </li>
          ))}
        </ul>
      )}
      {rows.length > 8 && (
        <div className="text-[11.5px] text-[#6E6E73] mt-2">+ {rows.length - 8} more flagged</div>
      )}
    </section>
  );
}
